import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Interface para as props de configuração
interface ConfigProps {
  stockLevel: number;
  batchSize: number;
  requestsPerSecond: number;
  handlingTimeOmd: number;
  providerSpecificHandlingTime: number;
  updateFlagValue: number;
  minimumStockThreshold?: number;
  enableMinimumStockCheck?: boolean;
  minimumPriceThreshold?: number;
  enableMinimumPriceCheck?: boolean;
  priceMarkupPercentage?: number; // Phase 2.1 - Markup % para cálculo de preço
}

interface BestBuyConfigProps {
  config: ConfigProps;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Formulário de configuração específico para Best Buy
 */
const BestBuyConfig: React.FC<BestBuyConfigProps> = ({ config, onChange }) => {
  const [quota, setQuota] = useState<any>(null);
  const [loadingQuota, setLoadingQuota] = useState(true);
  
  // Buscar quota ao montar o componente
  useEffect(() => {
    fetchQuota();
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchQuota, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchQuota = async () => {
    try {
      const response = await axios.get('/api/stores/bestbuy/quota');
      if (response.data.success) {
        setQuota(response.data.quota);
      }
    } catch (error) {
      console.error('Error fetching quota:', error);
    } finally {
      setLoadingQuota(false);
    }
  };
  
  const getQuotaColor = (percentage: number) => {
    if (percentage < 50) return 'bg-green-500';
    if (percentage < 75) return 'bg-yellow-500';
    if (percentage < 90) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  const getQuotaTextColor = (percentage: number) => {
    if (percentage < 50) return 'text-green-700';
    if (percentage < 75) return 'text-yellow-700';
    if (percentage < 90) return 'text-orange-700';
    return 'text-red-700';
  };
  
  return (
    <div className="space-y-4">
      {/* Quota Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-blue-900 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Daily API Quota
          </h3>
          <button
            onClick={fetchQuota}
            className="text-blue-600 hover:text-blue-800 text-sm"
            title="Refresh quota"
          >
            ↻ Refresh
          </button>
        </div>
        
        {loadingQuota ? (
          <div className="text-sm text-gray-600">Loading quota...</div>
        ) : quota ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-2xl font-bold ${getQuotaTextColor(quota.percentage)}`}>
                {quota.used.toLocaleString()} / {quota.limit.toLocaleString()}
              </span>
              <span className={`text-sm font-semibold ${getQuotaTextColor(quota.percentage)}`}>
                {quota.percentage.toFixed(1)}% used
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getQuotaColor(quota.percentage)}`}
                style={{ width: `${Math.min(quota.percentage, 100)}%` }}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                <strong>{quota.remaining.toLocaleString()}</strong> requests remaining
              </span>
              <span className="text-gray-500">
                Resets at midnight
              </span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600">No quota data available</div>
        )}
      </div>
      
      <div>
        <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700 mb-1">
          Nível de Estoque Best Buy
        </label>
        <input
          id="stockLevel"
          type="number"
          name="stockLevel"
          value={config.stockLevel || ''}
          onChange={onChange}
          className="input"
          aria-label="Nível de estoque Best Buy"
        />
        <p className="text-xs text-gray-500 mt-1">
          Quantidade mínima de estoque para produtos Best Buy.
        </p>
      </div>
      
      <div>
        <label htmlFor="handlingTimeOmd" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio OMD (dias)
        </label>
        <input
          id="handlingTimeOmd"
          type="number"
          name="handlingTimeOmd"
          value={config.handlingTimeOmd || ''}
          onChange={onChange}
          className="input"
          aria-label="Tempo de manuseio da OMD em dias"
        />
        <p className="text-xs text-gray-500 mt-1">
          Tempo adicional de manuseio pela OMD.
        </p>
      </div>
      
      <div>
        <label htmlFor="bestbuyHandlingTime" className="block text-sm font-medium text-gray-700 mb-1">
          Tempo de Manuseio Best Buy (dias)
        </label>
        <input
          id="bestbuyHandlingTime"
          type="number"
          name="providerSpecificHandlingTime"
          value={config.providerSpecificHandlingTime || ''}
          onChange={onChange}
          className="input"
          aria-label="Tempo de manuseio Best Buy em dias"
        />
        <p className="text-xs text-gray-500 mt-1">
          Tempo de manuseio específico da Best Buy.
        </p>
      </div>
      
      <div>
        <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
          Tamanho do Lote
        </label>
        <input
          id="batchSize"
          type="number"
          name="batchSize"
          value={config.batchSize || ''}
          onChange={onChange}
          className="input"
          aria-label="Tamanho do lote"
        />
        <p className="text-xs text-gray-500 mt-1">
          Produtos processados por lote (Amazon usará sempre 9990).
        </p>
      </div>
      
      <div>
        <label htmlFor="requestsPerSecond" className="block text-sm font-medium text-gray-700 mb-1">
          Requisições por Segundo
          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Recomendado: 20
          </span>
        </label>
        <input
          id="requestsPerSecond"
          type="number"
          name="requestsPerSecond"
          value={config.requestsPerSecond || ''}
          onChange={onChange}
          className="input"
          aria-label="Requisições por segundo"
          min="1"
          max="20"
        />
        <div className="mt-2 space-y-1">
          <p className="text-xs text-gray-500">
            Limite de requisições API por segundo. Baseado em teste de capacidade:
          </p>
          <div className="text-xs space-y-1">
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                ✅ Ideal: 20 RPS
              </span>
              <span className="text-gray-600">100% sucesso, processamento mais rápido</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                🛡️ Conservador: 15 RPS
              </span>
              <span className="text-gray-600">Para ambientes com alta latência</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                ⚠️ Mínimo: 5 RPS
              </span>
              <span className="text-gray-600">Apenas se houver problemas de conectividade</span>
            </div>
          </div>
        </div>
      </div>
      
      <div>
        <label htmlFor="minimumStockThreshold" className="block text-sm font-medium text-gray-700 mb-1">
          Estoque Mínimo (unidades)
        </label>
        <input
          id="minimumStockThreshold"
          type="number"
          name="minimumStockThreshold"
          value={config.minimumStockThreshold || ''}
          onChange={onChange}
          className="input"
          aria-label="Estoque mínimo"
          min="0"
        />
        <p className="text-xs text-gray-500 mt-1">
          Produtos com quantidade abaixo deste valor serão marcados como "Out of Stock".
        </p>
      </div>
      
      <div>
        <label htmlFor="enableMinimumStockCheck" className="flex items-center space-x-2">
          <input
            id="enableMinimumStockCheck"
            type="checkbox"
            name="enableMinimumStockCheck"
            checked={config.enableMinimumStockCheck || false}
            onChange={onChange}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Ativar verificação de estoque mínimo</span>
        </label>
      </div>
      
      <div>
        <label htmlFor="minimumPriceThreshold" className="block text-sm font-medium text-gray-700 mb-1">
          Preço Mínimo ($)
        </label>
        <input
          id="minimumPriceThreshold"
          type="number"
          name="minimumPriceThreshold"
          value={config.minimumPriceThreshold || ''}
          onChange={onChange}
          className="input"
          aria-label="Preço mínimo"
          min="0"
          step="0.01"
        />
        <p className="text-xs text-gray-500 mt-1">
          Produtos com preço abaixo deste valor serão marcados como "Out of Stock".
        </p>
      </div>
      
      <div>
        <label htmlFor="enableMinimumPriceCheck" className="flex items-center space-x-2">
          <input
            id="enableMinimumPriceCheck"
            type="checkbox"
            name="enableMinimumPriceCheck"
            checked={config.enableMinimumPriceCheck || false}
            onChange={onChange}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Ativar verificação de preço mínimo</span>
        </label>
      </div>
      
      <div>
        <label htmlFor="updateFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
          Código de Atualização Best Buy
        </label>
        <input
          id="updateFlagValue"
          type="number"
          name="updateFlagValue"
          value={config.updateFlagValue || ''}
          onChange={onChange}
          className="input"
          aria-label="Código de atualização Best Buy"
        />
        <p className="text-xs text-gray-500 mt-1">
          ID único para a Best Buy (4). Não altere esse valor a menos que seja absolutamente necessário.
        </p>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-4">
        <h3 className="text-blue-800 font-medium mb-2">Informações Best Buy</h3>
        <p className="text-sm text-blue-700 mb-2">
          O tempo total de manuseio (handling) que será enviado para a Amazon será a soma do tempo de manuseio da OMD
          e do tempo de manuseio específico da Best Buy.
        </p>
      </div>

      <div className="bg-green-50 p-4 rounded-md border border-green-200 mt-4">
        <h3 className="text-green-800 font-medium mb-2">🚀 Otimização de Performance</h3>
        <div className="text-sm text-green-700 space-y-2">
          <p>
            <strong>Teste de Capacidade Realizado:</strong> A API Best Buy foi testada e suporta até 20 RPS com 100% de sucesso.
          </p>
          <p>
            <strong>Benefícios do 20 RPS:</strong>
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Processamento 2.5x mais rápido que configurações anteriores</li>
            <li>Elimina problemas de rate limiting</li>
            <li>Reduz produtos incorretamente marcados como "OutOfStock"</li>
            <li>Sincronização completa em ~76 segundos (vs 3-4 minutos)</li>
          </ul>
        </div>
      </div>

      {/* Seção: Phase 2.1 - Price Markup */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-purple-900 mb-3">
          💰 Phase 2.1 - Price Markup
        </h3>
        
        <div>
          <label htmlFor="priceMarkupPercentage" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Percentage (%)
          </label>
          <input
            id="priceMarkupPercentage"
            type="number"
            name="priceMarkupPercentage"
            min="0"
            step="0.1"
            value={config.priceMarkupPercentage || 15}
            onChange={onChange}
            className="input"
          />
          <p className="text-sm text-gray-500 mt-1">
            Percentual de markup aplicado ao calcular o preço Amazon.
          </p>
          <p className="text-sm text-purple-700 mt-2">
            <strong>ℹ️ Fórmula:</strong> Amazon Price = (supplier_price + shipping) × (1 + markup% / 100)
          </p>
          <p className="text-sm text-purple-700 mt-1">
            <strong>📝 Exemplo:</strong> Produto $10 + Shipping $5 com 15% markup = $17.25
          </p>
        </div>
      </div>
    </div>
  );
};

export default BestBuyConfig; 